import { useEffect } from "react";

const SITE = "Boursicote";
const BASE_URL = "https://www.boursicote.fr";

interface SeoOptions {
  /** Page title WITHOUT the site suffix (we append " — Boursicote"). */
  title: string;
  description?: string;
  /** Path for the canonical URL, e.g. "/forums". Defaults to current path. */
  canonicalPath?: string;
  /** When true, emit <meta name="robots" content="noindex,nofollow">. */
  noindex?: boolean;
}

function upsertMeta(selector: string, attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * useSeo — sets the document <title>, meta description, canonical and OG/Twitter
 * title+description per page. Lightweight (no react-helmet). The static defaults
 * in index.html cover first paint / crawlers that don't run JS; this refines per
 * route once React mounts.
 */
export function useSeo({ title, description, canonicalPath, noindex }: SeoOptions) {
  useEffect(() => {
    const fullTitle = title.includes(SITE) ? title : `${title} — ${SITE}`;
    document.title = fullTitle;

    if (description) {
      upsertMeta('meta[name="description"]', "name", "description", description);
      upsertMeta('meta[property="og:description"]', "property", "og:description", description);
      upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    }
    upsertMeta('meta[property="og:title"]', "property", "og:title", fullTitle);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", fullTitle);

    const path = canonicalPath ?? window.location.pathname;
    const canonical = `${BASE_URL}${path}`;
    upsertCanonical(canonical);
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);

    // robots: noindex for private pages, otherwise index/follow.
    upsertMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large",
    );

    return () => {
      // On unmount, restore the indexable default so SPA navigation away from a
      // noindex page doesn't leave a stale noindex behind.
      if (noindex) {
        upsertMeta(
          'meta[name="robots"]',
          "name",
          "robots",
          "index, follow, max-image-preview:large",
        );
      }
    };
  }, [title, description, canonicalPath, noindex]);
}
